import { AlertTriangle, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useTaskManager, type TaskState } from '@/contexts/TaskManagerContext'
import { cn } from '@/lib/utils'
import { getTaskLabel } from '@/lib/task-labels'

interface TaskProgressBannerProps {
  task: TaskState
  /** Additional CSS classes */
  className?: string
}

/**
 * A banner component showing task progress at the top of a page.
 * Used on DesignDetailPage and BatchDetailPage to show ongoing tasks.
 */
export function TaskProgressBanner({ task, className }: TaskProgressBannerProps) {
  const { cancelTask } = useTaskManager()

  const canCancel = task.status === 'pending'

  const handleCancel = async () => {
    await cancelTask(task.taskId)
  }

  const getStatusText = () => {
    if (task.status === 'pending') return 'Queued'
    if (task.status === 'processing') return 'Processing'
    if (task.status === 'completed') return 'Completed'
    return 'Failed'
  }

  // Don't render if task is completed or failed
  if (task.status === 'completed' || task.status === 'failed') {
    return null
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/10 p-4',
        className
      )}
    >
      <div className="flex items-start gap-4">
        {/* Spinner */}
        <div className="flex-shrink-0 pt-0.5">
          <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--info))]" />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-foreground">{getTaskLabel(task.taskType, 'progress')}</h3>
            <span className="text-sm text-[hsl(var(--info))]">{getStatusText()}</span>
          </div>

          <p className="mt-1 text-sm text-muted-foreground">{task.progressMessage}</p>

          {/* Progress bar */}
          <div className="mt-3 flex items-center gap-3">
            <Progress value={task.progress} className="h-2 flex-1" />
            <span className="flex-shrink-0 text-sm font-medium text-foreground">
              {task.progress}%
            </span>
          </div>

          {/* Batch progress details */}
          {task.batchProgress && (
            <p className="mt-2 text-sm text-muted-foreground">
              {task.batchProgress.completed} of {task.batchProgress.total} designs completed
              {task.batchProgress.failed > 0 && (
                <span className="text-destructive"> ({task.batchProgress.failed} failed)</span>
              )}
            </p>
          )}
        </div>

        {/* Cancel button */}
        {canCancel && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-[hsl(var(--info))] hover:bg-[hsl(var(--info))]/20 hover:text-foreground"
            onClick={() => {
              void handleCancel()
            }}
            title="Cancel task"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * Batch-specific progress banner with additional stats.
 */
interface BatchProgressBannerProps {
  batchId: string
  batchName: string
  total: number
  completed: number
  failed: number
  inProgress: number
  progressPercent: number
  isRateLimited?: boolean
  className?: string
}

export function BatchProgressBanner({
  batchId: _batchId,
  batchName,
  total,
  completed,
  failed,
  inProgress,
  progressPercent,
  isRateLimited = false,
  className,
}: BatchProgressBannerProps) {
  const isComplete = completed + failed === total

  if (isComplete) {
    return null
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/10 p-4',
        className
      )}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 pt-0.5">
          <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--info))]" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-foreground">Processing Batch</h3>
            <span className="text-sm text-[hsl(var(--info))]">{batchName}</span>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <Progress value={progressPercent} className="h-2 flex-1" />
            <span className="flex-shrink-0 text-sm font-medium text-foreground">
              {progressPercent}%
            </span>
          </div>

          <div className="mt-2 flex flex-wrap gap-4 text-sm">
            <span className="text-muted-foreground">
              <span className="font-medium text-foreground">{completed}</span> completed
            </span>
            {inProgress > 0 && (
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">{inProgress}</span> in progress
              </span>
            )}
            {failed > 0 && (
              <span className="text-destructive">
                <span className="font-medium">{failed}</span> failed
              </span>
            )}
            <span className="text-muted-foreground">
              <span className="font-medium text-foreground">{total - completed - failed}</span> remaining
            </span>
          </div>

          {isRateLimited && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>Temporarily rate limited. Retrying with the latest saved batch progress.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
